// 사용자 행동 수집 클라이언트 — 페이지 진입·주요 클릭을 모아 서버(/api/v2/activity/events)로 보낸다.
//
// 설계 원칙:
//  1) 묶음 전송(batch) — 이벤트마다 요청을 날리지 않고 큐에 모았다가 (5초 주기 / 10개 / 탭 이탈) 한 번에.
//     화면 전환마다 네트워크가 터져 앱이 느려지는 걸 막는다.
//  2) 조용한 실패 — 수집은 부가 기능이라 절대 사용자 흐름을 막지 않는다. 실패하면 그냥 버린다(재시도 안 함).
//  3) 로그인 안 했으면 안 보낸다 — 세션 토큰이 없으면 큐를 비우고 끝(서버 401 낭비 방지).
//  4) userId/tenantId 는 안 보낸다 — 서버가 토큰에서 뽑아 박는다(위변조 방지). 프론트는 "무엇을" 만 보낸다.
//  5) 세션 첫 이벤트 앞에 session_start 한 건 — 어디서 왔나(referrer·utm)·무슨 기기로(화면 크기·user agent).
//     utm 은 로그인으로 주소가 바뀌기 전에 사라지므로 이 파일이 처음 읽힐 때(=첫 화면) 붙잡아 둔다(2026-10-05).

import { supabase } from "./supabase";
import { buildAuthHeaders } from "../api/_client";
import {
  redactActivityMeta,
  redactActivityPath,
  redactActivityText,
  referrerOrigin,
} from "../../shared/activity-redact";
import { normalizeActivityPath } from "./activityName";

// 서버 화이트리스트(server/src/v2/routes/activity.ts EVENT_TYPES)와 같은 목록이어야 한다 — 없으면 서버가 조용히 버린다.
export type ActivityType =
  | "pageview"
  | "click"
  | "filter"
  | "search"
  | "export"
  | "create"
  | "submit"
  | "session_start" // 탭 세션 첫 이벤트 — 유입 경로·기기
  | "rage_click"; // 같은 대상을 1초 안에 3번 이상 — 안 눌린다고 느낀 자리

type QueuedEvent = {
  type: ActivityType;
  path?: string | null;
  pageLabel?: string | null;
  target?: string | null;
  sessionId: string;
  meta?: Record<string, unknown> | null;
  ts: number; // 발생 시각(epoch ms) — 묶음 전송이라 실제 행동 시각을 함께 실어야 타임라인이 정확하다.
};

// 운영은 프론트·백엔드 도메인이 달라 절대주소 베이스가 필수(dev 는 빈 문자열 → vite 프록시).
// 빠뜨리면 운영에서 이벤트 전송이 404로 조용히 실패한다(수집이 통째로 죽음).
const ENDPOINT = `${import.meta.env.VITE_API_BASE_URL ?? ""}/api/v2/activity/events`;
const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE = 10;

// 탭 단위 세션 id — 같은 탭에서의 연속 행동을 서버가 한 "방문 흐름"으로 묶는 보조 열쇠.
// sessionStorage 라 새로고침해도 유지되고 탭을 닫으면 사라진다(탭=한 세션).
const SID_KEY = "pb_activity_sid";
const STARTED_KEY = "pb_activity_started"; // 이 세션의 session_start 를 이미 보냈나
const LANDING_KEY = "pb_activity_landing"; // 탭이 처음 열린 주소의 유입 정보(utm·referrer)

function getSessionId(): string {
  try {
    let sid = sessionStorage.getItem(SID_KEY);
    if (!sid) {
      sid = crypto?.randomUUID?.() ?? String(Date.now()) + Math.random().toString(36).slice(2);
      sessionStorage.setItem(SID_KEY, sid);
    }
    return sid;
  } catch {
    return "no-storage";
  }
}

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;

type Landing = { referrer: string | null; utm: Record<string, string>; landingPath: string | null };

/** 탭이 처음 열린 순간의 유입 정보를 붙잡아 둔다 — 로그인 리다이렉트가 주소의 utm 을 지우기 전에. */
function captureLanding(): void {
  if (typeof window === "undefined") return;
  try {
    if (sessionStorage.getItem(LANDING_KEY)) return;
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v) utm[k] = v.slice(0, 100);
    }
    const landing: Landing = {
      referrer: referrerOrigin(document.referrer), // 출처 도메인만 — 경로·쿼리엔 검색어가 실린다
      utm,
      landingPath: redactActivityPath(normalizeActivityPath(window.location.pathname).slice(0, 300)),
    };
    sessionStorage.setItem(LANDING_KEY, JSON.stringify(landing));
  } catch {
    /* 저장소가 막힌 브라우저 — 유입 정보 없이 간다 */
  }
}
captureLanding();

/** session_start 의 meta — 입력값·개인정보 없이 «어디서·무슨 기기로»만. */
function sessionStartMeta(): Record<string, unknown> {
  let landing: Partial<Landing> = {};
  try {
    landing = JSON.parse(sessionStorage.getItem(LANDING_KEY) ?? "{}") as Partial<Landing>;
  } catch {
    /* 없으면 빈 값 */
  }
  return {
    referrer: landing.referrer ?? null,
    utm: landing.utm ?? {},
    landingPath: landing.landingPath ?? null,
    screen: { w: window.screen?.width ?? null, h: window.screen?.height ?? null },
    viewport: { w: window.innerWidth, h: window.innerHeight },
    dpr: window.devicePixelRatio ?? null,
    userAgent: navigator.userAgent.slice(0, 300),
    language: navigator.language ?? null,
  };
}

/** 이 세션의 첫 이벤트인가 — 처음이면 표시를 남기고 true. 저장소가 막혔으면 보내지 않는다(매번 중복 방지). */
function claimSessionStart(): boolean {
  try {
    if (sessionStorage.getItem(STARTED_KEY) === getSessionId()) return false;
    sessionStorage.setItem(STARTED_KEY, getSessionId());
    return true;
  } catch {
    return false;
  }
}

/** 다른 사람으로 다시 로그인했을 때 새 세션으로 끊는다 — 앞사람 행동이 뒷사람 세션에 섞이지 않게. */
export function resetActivitySession(): void {
  queue = [];
  try {
    sessionStorage.removeItem(SID_KEY);
    sessionStorage.removeItem(STARTED_KEY);
  } catch {
    /* noop */
  }
}

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

function scheduleFlush() {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    void flush();
  }, FLUSH_INTERVAL_MS);
}

/** 큐를 서버로 비운다. keepalive 로 탭 이탈 순간에도 배달되게 한다. */
async function flush(useKeepalive = false): Promise<void> {
  if (queue.length === 0) return;
  // 로그인 안 했으면 보낼 수 없다 — 큐를 버리고 끝.
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    queue = [];
    return;
  }
  const batch = queue;
  queue = [];
  try {
    const headers = await buildAuthHeaders();
    headers.set("Content-Type", "application/json");
    await fetch(ENDPOINT, {
      method: "POST",
      headers,
      body: JSON.stringify({ events: batch }),
      keepalive: useKeepalive,
    });
  } catch {
    // 조용한 실패 — 수집은 부가기능. 재시도하지 않는다(무한 누적 방지).
  }
}

/** 이벤트 하나를 큐에 넣는다. 큐가 차면 즉시, 아니면 주기 전송으로 나간다. */
export function track(
  type: ActivityType,
  info: { path?: string | null; pageLabel?: string | null; target?: string | null; meta?: Record<string, unknown> | null } = {},
): void {
  const sessionId = getSessionId();
  const ts = Date.now();
  if (type !== "session_start" && typeof window !== "undefined" && claimSessionStart()) {
    queue.push({
      type: "session_start",
      sessionId,
      ts,
      path: redactActivityPath(info.path),
      meta: redactActivityMeta(sessionStartMeta()) as Record<string, unknown>,
    });
  }
  // 🔒 보내기 전에 가린다(서버도 저장 전에 같은 규칙으로 한 번 더) — 번호·이메일은 «[번호]»·«[이메일]»,
  // 경로는 쿼리스트링을 뗀다. 행동 기록은 S3 로 영구 백업되므로 여기서 새면 되돌릴 수 없다.
  queue.push({
    type,
    sessionId,
    ts,
    path: redactActivityPath(info.path),
    pageLabel: redactActivityText(info.pageLabel ?? null),
    target: redactActivityText(info.target ?? null),
    meta: (redactActivityMeta(info.meta ?? null) as Record<string, unknown> | null) ?? null,
  });
  if (queue.length >= MAX_QUEUE) {
    void flush();
  } else {
    scheduleFlush();
  }
}

/** 페이지 진입 기록 — 라우트가 바뀔 때마다 ActivityTracker 가 호출한다. */
export function trackPageview(path: string, pageLabel?: string | null): void {
  track("pageview", { path, pageLabel });
}

// 탭이 숨겨지거나(다른 탭 전환·닫기) 페이지가 사라질 때 남은 큐를 배달한다.
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flush(true);
  });
  window.addEventListener("pagehide", () => void flush(true));
}
